/**
 * @module app.shared
 */

import { observable, BindingEngine, autoinject, bindingMode } from 'aurelia-framework';
import { fireEventOn } from 'app/shared/lib/fire-events';
import { debounce } from 'app/shared/lib/decorators';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MatcherFunction, modelIdMatcher } from 'app/shared/lib/matcher';
import * as Core from 'app/core/models/essette-api';
import { ComponentBase } from 'app/shared/lib/essette-site';

export function hasSourceInfo(sourceInfo: Core.StagedContainer | null): boolean {
    return !!sourceInfo && (!!sourceInfo.fileName || !!sourceInfo.lineNumber || !!sourceInfo.columnNumber);
}

export function hasErrorMessages(errorContainer: Core.ErrorContainer | null): boolean {
    return !!errorContainer && (errorContainer.messages?.length || 0) > 0;
}

@autoinject()
export class ImportStagedTable extends ComponentBase {

    // table data, column definitions and row values
    @bindable.array({ changeHandler: 'setColumnData' }) columns: Array<Core.Column> = [];
    @bindable.array() rows: Array<Core.ImportTableRow> = [];

    @bindable.booleanAttr({ defaultBindingMode: bindingMode.oneTime }) paged = false;
    @bindable.booleanAttr() scrollable = true;

    @observable()
    public expanded: Array<Core.ImportTableRow> = [];

    protected columnMap: Map<string, number> | null = null;
    // Specifies the matcher function used to restore selected and expanded lists, and many other uses
    // where logic requires matching two objects.
    public matcher: MatcherFunction = modelIdMatcher;

    protected tableResizeObserver: ResizeObserver;
    protected fireEventOn = fireEventOn;

    @debounce()
    protected respondToTableResize() {
        if(this.$table) {
            this.$table.classList.add('ess-processing');
            this.$table.style.setProperty('--ess-table-width', `${this.element.scrollWidth}px`);
            this.$table.classList.remove('ess-processing');
        }
    };

    ////****************** Element References ***************************************////
    protected $table!: HTMLTableElement;
    protected $tableHeader!: HTMLTableRowElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(public readonly element: Element,
        ...rest: any[]) {

        super(...rest);
        this.tableResizeObserver = new ResizeObserver(this.respondToTableResize.bind(this));
    }

    protected attached() {
        this.respondToTableResize();
        this.connectTableObservers();
    }

    protected detached() {
        this.disconnectTableObservers();
        this.columnMap = null;
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    protected connectTableObservers() {
        if(this.$table) {
            this.tableResizeObserver.observe(this.element);
        }
    }

    protected disconnectTableObservers() {
        this.tableResizeObserver.disconnect();
    }

    protected setColumnData() {
        this.columnMap = new Map<string, number>();
        this.columns.forEach((column, index) => {
            if(!!column?.name) this.columnMap?.set(column.name, index);
        });
    }

    /**
     * Sorts an array of row values based on column position, leaving empty spaces where
     * a row is missing column data.
     * @param values Array of row values
     * @returns An array of row values sorted into proper column positions.
     */
    protected sortRowValues(values: Core.StagedValue[]): Array<Core.StagedValue> {
        const columnCount = this.columns.length;
        let sortedValues = new Array<Core.StagedValue>(columnCount);
        values.forEach((value) => {
            const valCol = value.columnName;
            if(!!valCol) {
                const columnIndex = this.columnMap?.get(valCol) ?? columnCount; // either the item is found or out of bounds
                if(columnIndex < columnCount) sortedValues[columnIndex] = value;
            }
        });

        return sortedValues;
    }

    protected isRowExpandable(row: Core.ImportTableRow): boolean {
        return hasSourceInfo(row.staged || null) || hasErrorMessages(row.error || null);
    }

    // Toggle collapsed state of a row by item or index;
    public toggleExpanded(item: Core.ImportTableRow): void;
    public toggleExpanded(index: number): void;
    public toggleExpanded(itemOrIndex: Core.ImportTableRow | number) {
        this.toggleInArray(itemOrIndex, this.expanded);
    }

    // Returns true if the row associated with an item or index is currently expanded.
    public isExpanded(item: Core.ImportTableRow): boolean;
    public isExpanded(index: number): boolean;
    public isExpanded(itemOrIndex: Core.ImportTableRow | number): boolean {
        const { item } = this.findInArray(itemOrIndex, this.expanded);
        return !!item && this.expanded.includes(item);
    }

    protected findInArray(itemOrIndex: Core.ImportTableRow | number, array: Array<Core.ImportTableRow>): { item: Core.ImportTableRow, index: number } {
        let item: any;
        let index: number;
        if(typeof (itemOrIndex) === 'number') {
            item = array[itemOrIndex];
            index = itemOrIndex;
        } else {
            item = itemOrIndex;
            index = array.findIndex(elem => this.matcher(elem, item));
        }
        return { item, index };
    }

    protected toggleInArray(itemOrIndex: Core.ImportTableRow | number, array: Array<Core.ImportTableRow>, include?: boolean): boolean | undefined {
        const { index, item } = this.findInArray(itemOrIndex, this.rows);
        if(!item || index === -1) {
            return;
        }
        const indexInArray = array.indexOf(item);
        if(indexInArray === -1) {
            if(include === true || include === undefined) {
                array.push(item);
                return true;
            }
        } else {
            if(include === false || include === undefined) {
                array.splice(indexInArray, 1);
                return false;
            }
        }
        return;
    }
}
